import { type ErrorCode, safeRedirect } from '@diario/common';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Alert } from '../components/ui/alert';
import { SpeechBubble } from '../components/ui/speechBubble';
import { sesionQueryOptions } from '../features/auth/api/useSesion';
import { DecoradoFranjaHoraria, FondoOscuroContext } from '../features/auth/components/decoradoFranjaHoraria';
import { FormularioAcceso } from '../features/auth/components/formularioAcceso';
import { AbanicoRoles } from '../features/roles/components/abanicoRoles';
import { traducirCodigo } from '../lib/errores';

interface BusquedaLogin {
	/** Código de error que el backend adjunta cuando el acceso con Google falla
	 * (`/login?error=ERR-012&redirect=…`, documento 02b §10.1). */
	error?: string;
	/** Con qué formulario abrir `FormularioAcceso` (`/login?modo=crearCuenta`, B7/H-11 de la
	 * auditoría funcional 2026-09-10). Único valor aceptado: `'crearCuenta'` — cualquier otro
	 * (incluido `undefined`) deja el modo por defecto del formulario ("entrar"). Quién manda a
	 * esta ruta con `?modo=crearCuenta` es tarea de otra oleada; aquí solo se prepara la lectura. */
	modo?: 'crearCuenta';
	/** Destino guardado antes de mandar a la persona aquí (`?redirect=`). Sin validar todavía:
	 * cada quien lo consume lo revalida con `safeRedirect` antes de usarlo. */
	redirect?: string;
}

/** Igual de laxo que cualquier búsqueda de URL: cadenas opcionales, sin más estructura que
 * comprobar. No hace falta un schema de ArkType para esto — no es un contrato con el backend,
 * es una lectura de `location.search`. */
const leerBusquedaLogin = (busqueda: Record<string, unknown>): BusquedaLogin => ({
	error: typeof busqueda.error === 'string' ? busqueda.error : undefined,
	modo: busqueda.modo === 'crearCuenta' ? 'crearCuenta' : undefined,
	redirect: typeof busqueda.redirect === 'string' ? busqueda.redirect : undefined,
});

/**
 * `/login` (documento 02b, §6 y §10.1). Rebote inverso (CA-02b.1): con sesión activa, esta
 * pantalla nunca se muestra — `beforeLoad` comprueba la sesión contra el servidor y, si existe,
 * redirige de vuelta a `?redirect=` (validado con `safeRedirect`) antes de renderizar nada.
 * Sin sesión (o sin poder comprobarla), no hace nada: esta ruta, a diferencia de
 * `_autenticado.tsx`, es la que SÍ debe quedarse abierta cuando la comprobación falla.
 */
export const Route = createFileRoute('/login')({
	beforeLoad: async ({ context, search }) => {
		try {
			await context.queryClient.ensureQueryData(sesionQueryOptions);
		} catch {
			return;
		}
		throw redirect({ href: safeRedirect(search.redirect) });
	},
	component: PantallaLogin,
	staticData: { anchoCompleto: true, rutaDeAcceso: true },
	validateSearch: leerBusquedaLogin,
});

/** Emoji decorativos de "prueba social" bajo el bocadillo del héroe (mock, `.login__avatars`:
 * ⛵ ⛰️ 🏕️ 🚂). Constante de módulo, así se leen desde una expresión
 * (`{AVATARES_PRUEBA_SOCIAL[…]}`), nunca como texto suelto en el JSX, y `i18next-cli lint` no los
 * confunde con una cadena de interfaz sin traducir. Sin el
 * contador «+18» del mock a propósito (decisión cerrada de esta tarea): es una cifra de comunidad
 * inventada, y ADR-0022 ya tomó el mismo criterio con la barra de cifras de `home.html` — el hueco
 * se deja vacío hasta que exista el endpoint de agregados, nunca se rellena con un número inventado. */
const AVATARES_PRUEBA_SOCIAL = ['⛵', '⛰️', '🏕️', '🚂'];

/**
 * El héroe de `/login` (T-4.2, mock `.login__hero`): titular a dos tintas, subtítulo y manifiesto
 * manuscritos, bocadillo con prueba social y avatares.
 *
 * **Por qué es un componente aparte y no JSX en línea dentro de `PantallaLogin` (T-5.2)**:
 * `FondoOscuroContext.Provider` vive DENTRO de `DecoradoFranjaHoraria`, envolviendo únicamente su
 * prop `children`. `PantallaLogin` es quien MONTA `<DecoradoFranjaHoraria>` — es antepasado de ese
 * Provider en el árbol, nunca descendiente — así que un `useContext(FondoOscuroContext)` en el
 * cuerpo de `PantallaLogin` leería siempre el valor por defecto de `createContext(false)` (claro),
 * jamás el real: los elementos de host que arma directamente (`<header>`, `<h1>`…) no difieren su
 * evaluación, a diferencia de un componente de React pasado como `children` — React no ejecuta el
 * cuerpo de ESE componente hasta que recorre esa parte del árbol, ya dentro del Provider. Es
 * exactamente el mecanismo por el que `FormularioAcceso` sí lee `oscuro` bien siendo, como
 * `HeroeLogin`, un componente propio en vez de JSX en línea de `routes/login.tsx`.
 *
 * **Contraste sobre el degradado**: `FondoOscuroContext` describe el PRIMER alto de `DEGRADADOS`
 * (`decoradoFranjaHoraria.tsx`) — oscuro en `atardecer`/`noche`, claro en `dia` — y es ese mismo
 * valor, ligado a la franja y no a dónde caiga `HeroeLogin` en el DOM, el que decide sus colores
 * (desde P-60 el héroe ya no es el primer bloque en móvil, ver más abajo; el criterio de contraste
 * no depende de esa posición). `oscuro` decide dos pares de color: `colorSobreDecorado`
 * (`text-text-on-dark` / `text-ink`, para el titular) y `colorAcentoSobreDecorado` (`text-grumete` /
 * `text-grumete-ink`, para el acento del
 * titular y el manuscrito de subtítulo/manifiesto) — mismo criterio de contraste que ya documenta
 * `COLOR_OSCURO`/`COLOR_CLARO` en `components/ui/statBlock.tsx`: `text-grumete-ink` (`#846a01`) es
 * tinta para ESCRIBIR sobre superficies claras (5,19:1 sobre blanco) y se apaga sobre `--da-ink`;
 * `text-grumete` (`#fed01a`, el crudo del rol) SÍ lee sobre `--da-ink` (7,7-10,4:1, mismo dato que
 * `COLOR_OSCURO.grumete`). El párrafo base de subtítulo y manifiesto (`text-gray-ink`) NO necesita
 * ese mismo tratamiento: `--color-gray-ink` apunta a `--da-gray-ink`, y `[data-fondo='oscuro']`
 * (`tokens.css`) ya lo redefine a `#a8a8a8` en cualquier descendiente del contenedor que declara
 * ese atributo — el propio `DecoradoFranjaHoraria` — así que se aclara solo, por cascada de
 * variables CSS, sin pasar por contexto de React. El bocadillo cambia de variante
 * (`variant={oscuro ? 'accent' : 'soft'}`, prop ya existente de `SpeechBubble`, fichero prohibido
 * en esta tarea): `soft` es un tinte traslúcido al 12 % con texto oscuro —ilegible si el degradado
 * de detrás es oscuro— así que en franja oscura se usa `accent` (fondo lima SÓLIDO + texto
 * oscuro), que no depende de lo que haya detrás. Los avatares (`bg-paper-2` opaco, borde
 * `border-border-soft`) no llevan tratamiento condicional: son chips opacos con su propio fondo
 * claro, así que ya contrastan contra cualquier degradado oscuro de detrás sin necesitar leer
 * `oscuro` — y el emoji de cada uno no depende de `color` (CSS) para pintarse. Verificado en las
 * tres franjas con el reloj fijado (`vi.setSystemTime`), no con la hora real — ver el describe de
 * contraste en `tests/loginRuta.test.tsx`.
 *
 * El teaser de roles de `PantallaLogin`, más abajo, NO sigue este mismo patrón (se queda en
 * `text-ink`/`text-gray-ink` fijos, sin `useContext`): es el último bloque antes de `sm:pb-28`, así
 * que siempre cae sobre la COLA del degradado, y las tres colas de `DEGRADADOS` son claras
 * (`surface`, `accent-deep`, `surface-app`) — ver el JSDoc de `DEGRADADOS` en
 * `decoradoFranjaHoraria.tsx`.
 *
 * **P-60 — el héroe ya no se recorta en móvil**: hasta P-60 el manifiesto (el
 * `<p className='max-w-xs…'>` de más abajo) y el bloque de bocadillo + avatares se ocultaban
 * (`hidden lg:block` / `hidden lg:flex`) por debajo de `lg`, para que «ENTRAR» quedara dentro del
 * primer pantallazo en un móvil de 375×667. El cliente no dio la razón a ese arreglo (respuesta,
 * 2026-09-17): «al portar un diseño se replica fiel, no se adelgaza… el problema que medisteis es
 * real, pero el arreglo es otro: cambiad el orden, no el contenido» — el manifiesto, el bocadillo y
 * las caras son el argumento de la pantalla, y ocultarlos en móvil es ocultarlos para la mayoría.
 * Las cuatro piezas del héroe se pintan siempre, en cualquier breakpoint (sin `hidden`); lo que
 * resuelve el hueco vertical en móvil es el orden del DOM, no la visibilidad — ver el JSDoc de
 * `PantallaLogin`, más abajo, para cómo cambia ese orden sin tocar la rejilla de escritorio.
 */
const HeroeLogin = (): React.JSX.Element => {
	const { t } = useTranslation('auth');
	const oscuro = useContext(FondoOscuroContext);
	const colorSobreDecorado = oscuro ? 'text-text-on-dark' : 'text-ink';
	const colorAcentoSobreDecorado = oscuro ? 'text-grumete' : 'text-grumete-ink';

	return (
		<header className='flex w-full flex-col items-center gap-2 text-center lg:col-start-1 lg:row-start-1'>
			<h1 className={`font-heading text-3xl ${colorSobreDecorado} leading-tight`}>
				<span className={colorAcentoSobreDecorado}>{t('heroe.titulo.acento')}</span> {t('heroe.titulo.resto')}
			</h1>

			<p className='text-base text-gray-ink'>
				<Trans
					components={{ mano: <em className={`font-hand ${colorAcentoSobreDecorado} text-xl not-italic`} /> }}
					i18nKey='heroe.lead'
					ns='auth'
				/>
			</p>

			<p className='max-w-xs text-gray-ink text-sm leading-relaxed'>
				<Trans
					components={{ mano: <em className={`font-hand ${colorAcentoSobreDecorado} text-lg not-italic`} /> }}
					i18nKey='heroe.manifiesto'
					ns='auth'
				/>
			</p>

			<div className='mt-4 flex flex-col items-center gap-2'>
				<SpeechBubble tail='none' variant={oscuro ? 'accent' : 'soft'}>
					{t('heroe.bocadillo')}
				</SpeechBubble>

				<span aria-hidden='true' className='flex'>
					{AVATARES_PRUEBA_SOCIAL.map((emoji, indice) => (
						<i
							className={`grid h-7 w-7 place-items-center rounded-pill border border-border-soft bg-paper-2 text-sm not-italic ${
								indice === 0 ? '' : '-ml-2'
							}`}
							key={emoji}
						>
							{emoji}
						</i>
					))}
				</span>
			</div>
		</header>
	);
};

/**
 * `DecoradoFranjaHoraria` como único marco de la pantalla, con TODO dentro (T-5.2, mock
 * `design-system/ui_kits/app/screens/login.html`: `.login__frame` contiene `.login__sky` —cielo y
 * montañas, ya pintados por el propio `DecoradoFranjaHoraria`— y, como hijos directos de
 * `.login__content`, `.login__hero`, `.login__card` y `.login__roles`): la tarjeta de
 * `FormularioAcceso`, `HeroeLogin` y, por último, el teaser de roles con `AbanicoRoles`. Desde P-60
 * (respuesta del cliente, 2026-09-17) el ORDEN DEL DOM ya no calca literalmente el del mock
 * (`.login__hero` antes que `.login__card`): en móvil el formulario va primero —era el problema
 * real que medía la auditoría, «ENTRAR» fuera del primer pantallazo— y el héroe completo (titular,
 * subtítulo, manifiesto, bocadillo y caras, sin recortar ninguna pieza) va justo debajo, seguido
 * del teaser de roles. Desde `lg` el dibujo vuelve a ser exactamente el del mock: la rejilla de dos
 * columnas (T-A, más abajo) coloca cada bloque por coordenada (`lg:col-start-*`/`lg:row-start-*`),
 * así que el orden del DOM no afecta a lo que se ve ahí. Hasta T-5.2 el héroe vivía ANTES del
 * decorado (sobre el gris de `<main>`, nunca sobre el degradado) y el teaser DESPUÉS (T-4.2) — las
 * montañas de `DecoradoFranjaHoraria`, ancladas al pie de su propia caja (`bottom-0` del SVG),
 * quedaban entonces escondidas bajo la `Card` opaca del formulario en vez de leerse detrás del
 * abanico de roles, que es donde las pinta el mock. `AbanicoRoles` lleva su propio CTA
 * `<Link to='/roles'>` a la senda completa (`routes/roles.tsx`) — no se embebe `SendaRoles` entera
 * (ocho paradas, dos carruseles, nueve preguntas frecuentes), que quedó fuera de lugar en una
 * pantalla de acceso rápido; su destino natural es la ruta propia. Al terminar con sesión creada,
 * esta pantalla navega ella misma al destino guardado — `FormularioAcceso` no navega por su cuenta.
 *
 * `auth:pantalla.titulo`/`auth:contexto.generico`/`auth:contexto.destinoUnirse` NO se retiran del
 * namespace `auth` aunque este fichero ya no los use: siguen en uso real por `FormularioAcceso`
 * (congelado, `t('pantalla.titulo')`, `t('contexto.generico')`, `t('contexto.destinoUnirse')`) y
 * por `formularioRestablecerContrasena.tsx` (`t('contexto.restablecer')`), así que
 * `i18next-cli status --unused` no los marca huérfanos — borrarlos rompería el `typecheck` de esos
 * dos ficheros congelados.
 *
 * F-1: el decorado deja de ser una tarjeta posada sobre `bg-surface-app` y pasa a pintarse a
 * sangre completa (`__root.tsx` marca esta ruta como `anchoCompleto`: el contenedor de lectura de
 * `<main>` desaparece y `DecoradoFranjaHoraria` ocupa todo el ancho del viewport, con
 * `min-h-full` estirándolo como mínimo al alto de la ventana bajo la cabecera). El formulario, el
 * héroe y el teaser de roles, ya dentro de ese marco a sangre, se leen a la anchura de "columna"
 * (`max-w-md`) por debajo de `lg`; desde `lg` (T-A, ver el párrafo de la rejilla más abajo) pasan a
 * una rejilla de dos columnas de hasta `max-w-5xl` (1024 px), así que dejan de ensancharse al ancho
 * completo del viewport en cualquier breakpoint. El relleno del decorado (ver `className` más
 * abajo) ya no cancela nada de `<main>` —no hay padding que cancelar, `<main>` no lo tiene en esta
 * ruta— así que es simétrico entre eje horizontal y vertical superior en cada breakpoint: `px-4`/
 * `pt-4` por debajo de `sm`, `sm:px-6`/`sm:pt-6` desde ahí.
 *
 * El relleno inferior (`pb-24`/`sm:pb-28`) es generoso a propósito para que la silueta de montañas
 * (`h-20 sm:h-24`, anclada al pie del decorado) no se coma el teaser de roles — desde P-62 ya no
 * hay ninguna fila de selector cuyo alto compensar (la compensación de H-32, `sm:pt-0 sm:pb-14`, ya
 * había desaparecido en F-1 porque tampoco hay ningún `py-14` de tarjeta que conservar).
 *
 * **T-A — rejilla de dos columnas desde `lg`**: hasta esa tarea el héroe, la tarjeta del formulario
 * y el teaser eran tres bloques sueltos dentro de `DecoradoFranjaHoraria`, cada uno con su propio
 * `mx-auto max-w-md` y separados por `mt-10` — no `gap-10` de un `flex flex-col` envolvente: un
 * hijo con márgenes automáticos en el eje transversal (el `mx-auto` de cada bloque) deja de
 * estirarse dentro de un flex-item y se encoge a su contenido (spec de Flexbox §8.3). En un
 * viewport de 885 px de alto la página medía 1369 px y nunca se veía entera; en 375×667 el botón
 * «ENTRAR» caía en `y = 662`, fuera del primer pantallazo — el mismo problema que P-60 vuelve a
 * medir más abajo, ya sin tocar la rejilla de aquí.
 *
 * La solución no reintroduce ese `flex flex-col` directamente sobre los tres bloques: los mete
 * dentro de UN envoltorio (el `<div>` que los agrupa, más abajo) que sí es flex/grid, y son los
 * tres bloques —no el envoltorio— quienes pierden su `mx-auto max-w-md` propio (ganan `w-full` en
 * su lugar: el ancho ya lo fija el envoltorio). Ese envoltorio es el ÚNICO `mx-auto` legítimo de
 * esta zona, y sobrevive porque declara `w-full`: desde `lg` la raíz de `DecoradoFranjaHoraria` SÍ
 * es `flex flex-col` (para centrar verticalmente con el `lg:my-auto` de abajo), así que el
 * envoltorio es un flex-item — pero con un ancho propio definido, el `stretch` no decide nada y el
 * `mx-auto` se limita a centrarlo. Quien añada aquí un hermano sin `w-full` sí reproducirá la
 * trampa que ya pagó dos veces este repositorio (la tarjeta de acceso, encogida de 448 a ~292 px; el
 * aviso de `__root.tsx`/`avisoModoVisitante.tsx`, de 768 a 749 px), documentada aquí de nuevo porque
 * ningún hijo colocado por coordenada (`lg:col-start-*`) puede volver a llevar `mx-auto` sin
 * reproducirla.
 *
 * Por debajo de `lg` el envoltorio es una columna (`flex-col gap-10`, los mismos 40 px que daba el
 * `mt-10` de antes); desde `lg` pasa a `grid-cols-[minmax(0,1fr)_448px]` (448 px, el ancho fijo que
 * ya tenía la tarjeta del formulario) con los tres bloques colocados por coordenadas explícitas
 * (`lg:col-start-*`/`lg:row-start-*`) en vez de depender del orden del DOM para el dibujo de
 * escritorio — así que reordenar el DOM (P-60, debajo) no afecta a nada desde `lg`: la rejilla
 * sigue pintando el héroe arriba a la izquierda, el formulario a la derecha abarcando las dos filas
 * y el teaser abajo a la izquierda, exactamente el dibujo del mock.
 *
 * **P-60 — el formulario va primero en móvil**: hasta esta tarea el héroe se compactaba por debajo
 * de `lg` (ocultaba manifiesto, bocadillo y caras — ver el JSDoc de `HeroeLogin`) para que «ENTRAR»
 * cupiera en el primer pantallazo. El cliente no dio la razón a ese arreglo (respuesta,
 * 2026-09-17): un diseño se porta fiel, no se adelgaza; el arreglo correcto es reordenar, no ocultar
 * contenido. La solución real es el orden del DOM, no la visibilidad: por debajo de `lg`, donde el
 * envoltorio es un `flex-col` sin coordenadas de rejilla, el ORDEN DE ESCRITURA decide el orden
 * visual — así que el bloque del formulario se escribe antes que `<HeroeLogin />` en el JSX (más
 * abajo), y el teaser sigue último. Ninguno de los tres bloques gana un `mx-auto` propio al
 * moverse (seguiría cayendo en la trampa de arriba): los tres conservan `w-full` y sus coordenadas
 * `lg:col-start-*`/`lg:row-start-*`, así que desde `lg` el dibujo no cambia — la rejilla, no el
 * orden del DOM, es la que manda ahí (párrafo de arriba).
 */
function PantallaLogin(): React.JSX.Element {
	const { t } = useTranslation(['auth', 'common']);
	const navigate = useNavigate();
	const { error, modo, redirect: destino } = Route.useSearch();
	// `traducirCodigo` (`lib/errores.ts`, congelado) siempre devuelve un `ErrorCode` válido, con
	// `ERROR_CODES.internal` como respaldo para un código que este build no reconoce — pero aquí, a
	// diferencia del resto de consumidores, un código SIN traducción conocida no debe mostrar el
	// aviso genérico de `ERR-000`: debe seguir sin mostrar ningún aviso, exactamente como antes de
	// esta migración. Comparar contra el propio `error` recupera esa semántica sin duplicar el
	// conjunto de códigos conocidos en este fichero: solo coincide cuando `error` YA era un código
	// reconocido (`traducirCodigo` lo devuelve intacto), nunca cuando cayó al respaldo `internal`.
	const codigoError = error !== undefined && traducirCodigo(error) === error ? (error as ErrorCode) : undefined;

	return (
		<DecoradoFranjaHoraria className='px-4 pt-4 pb-24 sm:px-6 sm:pt-6 sm:pb-28 lg:pb-16'>
			<div className='mx-auto flex w-full max-w-md flex-col gap-10 lg:grid lg:max-w-5xl lg:grid-cols-[minmax(0,1fr)_448px] lg:items-start lg:gap-x-12 lg:my-auto'>
				<div className='flex w-full flex-col gap-8 lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:self-center'>
					{codigoError ? <Alert variante='error'>{t(`common:errores.${codigoError}`)}</Alert> : null}

					<FormularioAcceso
						destino={destino}
						modoInicial={modo}
						onAccesoConSesion={() => void navigate({ href: safeRedirect(destino) })}
					/>
				</div>

				<HeroeLogin />

				<section className='flex w-full flex-col items-center gap-1 text-center lg:col-start-1 lg:row-start-2'>
					<h2 className='font-heading text-ink text-lg'>{t('teaserRoles.titulo')}</h2>
					<p className='text-gray-ink text-sm'>{t('teaserRoles.subtitulo')}</p>
					<AbanicoRoles className='mt-4' />
				</section>
			</div>
		</DecoradoFranjaHoraria>
	);
}
